import { useEffect, useState, useContext, createContext, useCallback, useMemo } from 'react'
import { CognitoUser, CognitoIdToken, CognitoUserSession } from 'amazon-cognito-identity-js'
import { Hub } from '@aws-amplify/core'
import { Auth, CognitoHostedUIIdentityProvider } from '@aws-amplify/auth'
import { hubCallback } from '../config/amplify/hub'
import { setRequestInterceptor } from '../config/axios'
import { configureAmplify } from '../config/amplify/auth'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { showNotification } from '@mantine/notifications'
import { useCognitoInfo } from 'api/query/cognito'
import { UserToken } from 'api/dto/user-token'
import { Role } from 'api/dto/role'
import { globalQueryClient } from 'api/client'

interface ICognitoUser {
  email: string
  given_name: string
  family_name: string
  organization: string
}

const authContext = createContext<ReturnType<typeof useProvideAuth>>({} as never)

export function ProvideAuth(): JSX.Element {
  const currentAuth = useProvideAuth()
  return (
    <authContext.Provider value={currentAuth}>
      <Outlet />
    </authContext.Provider>
  )
}

export const useAuth = () => useContext(authContext)

const useProvideAuth = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const { data: infos, isLoading: isInfosLoading } = useCognitoInfo()

  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [tempEmail, setTempEmail] = useState('')
  const [federatedSignInLoading, setFederatedSignIn] = useState(false)
  const [isHydrated, setIsHydrated] = useState(false)
  const [tempCognitoUser, setTempCognitoUser] = useState<CognitoUser>()

  const [user, setUser] = useState<UserToken | null>(null)
  const [token, setToken] = useState<string | null>(null)

  const appIsHydrated = useMemo(() => isHydrated, [isHydrated])

  const logout = useCallback(async (): Promise<void> => {
    await Auth.signOut()
    globalQueryClient.removeQueries()
    setIsAuthenticated(false)
    navigate('/session/login')
  }, [navigate])

  const extractAndRedirect = useCallback(async () => {
    const session = await Auth.currentSession()
    setRequestInterceptor(logout)

    if (!session) {
      return
    }

    const user = extractUserFromToken(session.getIdToken())

    setUser(user)
    setIsAuthenticated(true)

    if (location.pathname.includes('session')) {
      navigate('/app/dashboard')
    }
  }, [location, logout, navigate])

  const hydrateUser = useCallback(async () => {
    if (isInfosLoading) {
      return
    }

    configureAmplify(infos)

    try {
      await extractAndRedirect()
    } catch (error) {
      // console.log("no user")
    }

    if (location.pathname.includes('/change-password') && !tempCognitoUser) {
      navigate('/session/login')
    }

    setIsHydrated(true)
  }, [isInfosLoading, infos, extractAndRedirect, tempCognitoUser, location, navigate])

  const login = async (email: string, password: string): Promise<void> => {
    try {
      const cognitoUser: CognitoUser & { challengeName: string } = await Auth.signIn({ username: email.toLowerCase(), password })

      if (cognitoUser.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setTempCognitoUser(cognitoUser)
        navigate('/session/change-password')
        return
      }

      const session = await Auth.currentSession()

      if (session) {
        setRequestInterceptor(logout)
        setIsAuthenticated(true)
        setUser(extractUserFromToken(session.getIdToken()))

        navigate('/app/dashboard')
      } else {
        showNotification({
          title: 'Login Failed',
          message: 'Seems like the email or password is not valid, please try again.',
          color: 'red',
        })
      }
    } catch (error: any) {
      showNotification({
        title: 'Login Failed',
        message: error.message,
        color: 'red',
      })
    }
  }

  const signUp = async (email: string, password: string, user: ICognitoUser) => {
    const { organization, ...cognitoUser } = user

    await Auth.signUp({
      username: email.toLowerCase().trim(),
      password,
      attributes: {
        ...cognitoUser,
      },
      clientMetadata: {
        organization: user.organization,
      },
    })

    // if (import.meta.env.VITE_ENV !== 'PROD' && import.meta.env.VITE_ENV !== 'DEV') {
    await login(email, password)
    // }
  }

  const completeNewPassword = useCallback(
    async (newPassword: string): Promise<void> => {
      if (!tempCognitoUser) {
        return
      }

      try {
        await Auth.completeNewPassword(tempCognitoUser, newPassword, {})
        showNotification({
          message: 'Password has been changed successfully',
          color: 'green',
        })
        await extractAndRedirect()
      } catch (error: any) {
        if (error.code === 'Code mismatch') {
          showNotification({
            message: 'Code does not match',
            color: 'red',
          })
        }
        showNotification({
          message: 'Change password failed, try again',
          color: 'red',
        })
      }
    },
    [tempCognitoUser, extractAndRedirect],
  )

  const forgotPassword = useCallback(
    async (email: string): Promise<void> => {
      const currentEmail = email.toLowerCase().trim()

      // we set tempAuth for further use in resetPassword
      setTempEmail(currentEmail)

      try {
        await Auth.forgotPassword(currentEmail)
        showNotification({
          title: 'Forgot Password',
          message: 'A code has been sent to your email, please check your email.',
          color: 'blue',
        })

        navigate('/session/reset-password')
      } catch (error) {
        showNotification({
          title: 'Forgot Password',
          message: 'Seems like the email is not valid, please enter a valid email.',
          color: 'red',
        })
      }
    },
    [navigate],
  )

  const resetPassword = useCallback(
    async (verificationCode: string, newPassword: string): Promise<void> => {
      if (!tempEmail) {
        return
      }

      try {
        await Auth.forgotPasswordSubmit(tempEmail, verificationCode, newPassword)
        showNotification({
          title: 'Change Password',
          message: 'Password has been changed successfully',
          color: 'green',
        })
        navigate('/session/login')
      } catch (error: any) {
        console.log(error)
        if (error.code === 'CodeMismatchException') {
          showNotification({
            title: 'Change Password',
            message: 'An error occurred, please try again',
            color: 'red',
          })
        }
        showNotification({
          title: 'Change Password',
          message: 'An error occurred, please try again',
          color: 'red',
        })
      }
    },
    [tempEmail, navigate],
  )

  const signInWithGoogle = async () => {
    setFederatedSignIn(true)
    await Auth.federatedSignIn({
      provider: CognitoHostedUIIdentityProvider.Google,
    })
  }

  const completeSignInWithGoogle = () => {
    setIsAuthenticated(true)
    setFederatedSignIn(false)
  }

  const refreshSession = async () => {
    const cognitoUser: CognitoUser = await Auth.currentAuthenticatedUser()
    const currentSession: CognitoUserSession | null = await Auth.currentSession()
    if (!currentSession) {
      throw new Error('No session')
    }

    return new Promise((resolve, reject) => {
      cognitoUser.refreshSession(currentSession.getRefreshToken(), (err, session: CognitoUserSession) => {
        if (err) {
          reject(err)
        }

        const user = extractUserFromToken(session.getIdToken())
        setUser(user)
        resolve(session)
      })
    })
  }

  const extractUserFromToken = (token: CognitoIdToken) => {
    const payload = token.payload as Record<string, string>

    setToken(token.getJwtToken())

    return new UserToken({
      id: payload.userId,
      email: payload.email,
      isAdmin: payload.isAdmin == 'true',
      currentOrgId: payload.currentOrgId,
      organizationId: payload.organizationId,
      role: payload.role as Role,
    })
  }

  useEffect(() => {
    void hydrateUser()
  }, [hydrateUser])

  useEffect(() => {
    const callback = hubCallback(completeSignInWithGoogle, logout)
    const removeAuthListener = Hub.listen('auth', callback)

    return () => removeAuthListener()
  }, [logout])

  return {
    user,
    login,
    refreshSession,
    completeNewPassword,
    logout,
    appIsHydrated,
    signUp,
    forgotPassword,
    resetPassword,
    isAuthenticated,
    signInWithGoogle,
    completeSignInWithGoogle,
    federatedSignInLoading,
    token,
  }
}
